/**
 * PortfolioDetails — the "Portfolio details" card on Manage Portfolio:
 * portfolio name, contract discount, template download and file upload.
 *
 * Ported from legacy ManageAccount.vue (name + Contract Discount%) and
 * UploadDownloadTemplate.vue (EIA branch), laid out to the new design.
 *
 * Controlled: the page owns the values (they are what Save will send), this
 * card only shows and edits them. Uploading hands the chosen file to
 * `onUpload` — the upload flow itself is the page's / API's job.
 *
 * Differences from legacy, by design:
 *  - Contract discount is the shared ContractDiscountButton, which opens
 *    ContractDiscountDialog (On Demand / 1 Year RI / 3 Year RI, whole numbers
 *    0–99, design 2026-10-21) rather than an always-visible field; the button
 *    shows the values once set. All three are saved, as pricing_model_discount.
 *  - One "Upload file" button opens the file picker directly; legacy needed a
 *    file input plus a separate Upload button. Non-.xlsx files are refused
 *    with a message instead of silently doing nothing.
 *  - A template is checked before it is saved (downloadTemplate.js): a
 *    missing file shows a message instead of saving the app's web page under
 *    the .xlsx name.
 *
 * Shared by CCA (one card, no twin — .claude/rules/variants.md). What CCA
 * changes comes in as optional props, so EIA, which passes none, is unchanged:
 *  - `templates` / `templateLabel`: CCA's own template list and its button
 *    label ("Template", or "Templates" in Billing mode — ccaFormRules);
 *  - `extra`: a node at the end of the row — CCA's "Cloud Usage Reports" link.
 */
import { useState } from 'react';

import { ContractDiscountButton } from '@components/common/ContractDiscountDialog/ContractDiscountButton';
import { TextField } from '@components/common/TextField';
import { ChevronDownIcon } from '@components/common/icons';
import { Button, Card, CardBody, Menu } from '@components/ui';
import { cx } from '@lib/classNames';

import { downloadTemplate } from '../downloadTemplate';
import { PORTFOLIO_NAME_HINT, templatesFor, validatePortfolioName } from '../portfolioFormRules';
import { UploadFileButton } from '../UploadFileButton';

import styles from './PortfolioDetails.module.css';

export function PortfolioDetails({
  name = '',
  onNameChange,
  nameReadOnly = false,
  /** { onDemand, oneYearRi, threeYearRi } — what Save sends as pricing_model_discount. */
  contractDiscount,
  onContractDiscountChange,
  provider,
  onUpload,
  /** Template downloads (legacy: needs the file-upload/validate permission). */
  showTemplates = true,
  /** Upload File is shown but off (the page is busy). */
  uploadDisabled = false,
  /** The templates offered ({ id, label, href, filename }[]); default: EIA's for `provider`. */
  templates,
  /** The template button's label. */
  templateLabel = 'Template',
  /** Extra content at the end of the row (CCA: the Cloud Usage Reports link). */
  extra = null,
}) {
  const [nameTouched, setNameTouched] = useState(false);
  // One message line for both file actions: a refused upload, or a template
  // that could not be downloaded.
  const [fileError, setFileError] = useState('');

  // Show "required" only after the user has been in the field; a bad
  // character is reported straight away.
  const nameError = validatePortfolioName(name);
  const shownNameError = name || nameTouched ? nameError : null;

  const templateItems = (templates ?? templatesFor(provider)).map((t) => ({
    id: t.id,
    label: t.label,
    onSelect: () => {
      setFileError('');
      downloadTemplate(t).catch((e) => setFileError(e.message));
    },
  }));

  return (
    <Card className={styles.card}>
      <CardBody className={styles.body}>
        <section className={styles.details} aria-labelledby="portfolio-details-title">
          <header className={styles.header}>
            <h2 id="portfolio-details-title" className={styles.title}>
              Portfolio Details
            </h2>
            <p className={styles.subtitle}>Name the portfolio and set the contract discount.</p>
          </header>

          {/* hideDetails: the field's reserved message row would sit under the
              input and push the bottom-aligned buttons below it. The error is
              shown under the whole row instead (see .nameError). */}
          <div className={styles.row}>
            <TextField
              id="portfolio-name"
              className={styles.name}
              inputClassName={cx(shownNameError && styles.controlError)}
              label="Portfolio Name"
              required
              placeholder="Enter Portfolio Name"
              value={name}
              readOnly={nameReadOnly}
              onChange={(e) => onNameChange?.(e.target.value)}
              onBlur={() => setNameTouched(true)}
              title={PORTFOLIO_NAME_HINT}
              error={shownNameError ?? undefined}
              hideDetails
              aria-describedby={shownNameError ? 'portfolio-name-error' : undefined}
              autoComplete="off"
            />

            <div className={styles.actions}>
              <ContractDiscountButton
                className={styles.actionButton}
                value={contractDiscount}
                onApply={(value) => onContractDiscountChange?.(value)}
              />

              {showTemplates ? (
                <Menu
                  className={styles.menu}
                  align="right"
                  items={templateItems}
                  trigger={
                    <Button
                      variant="primary"
                      className={styles.actionButton}
                      endIcon={<ChevronDownIcon />}
                    >
                      {templateLabel}
                    </Button>
                  }
                />
              ) : null}

              {/* Upload File — only when the page passes `onUpload`. By user
                  decision (2026-10-14) that is ONLY for an opened portfolio
                  with no instances: a new portfolio uploads from the "No
                  instances added yet" card, and legacy (UploadDownloadTemplate
                  .vue :15/:73) refuses a file into an opened portfolio that
                  already has instances. */}
              {onUpload ? (
                <UploadFileButton
                  className={styles.actionButton}
                  disabled={uploadDisabled}
                  onFile={(file) => {
                    setFileError('');
                    onUpload(file);
                  }}
                  onReject={setFileError}
                />
              ) : null}
            </div>

            {extra}
          </div>

          {/* Divs, not <p>: the library greys every <p> in a card body
              (.amd-card__body p), which beat the error's red — the name
              message showed grey (issue: Manage Portfolio labels). */}
          {shownNameError ? (
            <div id="portfolio-name-error" className={styles.error}>
              {shownNameError}
            </div>
          ) : null}

          {fileError ? (
            <div className={styles.error} role="alert">
              {fileError}
            </div>
          ) : null}
        </section>
      </CardBody>
    </Card>
  );
}

export default PortfolioDetails;
